import { useEffect, useState } from 'react';
import { t } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { money } from '../lib/format.ts';
import type { OrderView } from '../lib/types.ts';
import { useUi } from '../lib/ui-state.tsx';
import { Icon } from './Icon.tsx';
import { Button, Dialog, Field, Skeleton } from './ui.tsx';

interface Offers {
  current: { id: string; name: string; amount: number; stale: boolean } | null;
  offers: { id: string; name: string; kind: string; amount: number; why_not: string | null }[];
}

/**
 * At the till: the promotions open now for this bill, each with what it takes off, and a box for
 * a code the customer brings. One promotion per bill; picking another takes its place.
 */
export function PromoDialog({ view, onClose, onDone }: { view: OrderView; onClose: () => void; onDone: (v: OrderView) => void }) {
  const { fail, toast } = useUi();
  const [data, setData] = useState<Offers | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const order = view.order.id;
  useEffect(() => {
    action<Offers>('loyalty.offers', { order }).then(setData, fail);
  }, [order, fail]);
  const go = async (input: Record<string, unknown>, remove = false) => {
    setBusy(true);
    try {
      if (remove) await action('loyalty.remove_promotion', { order });
      else {
        const r = await action<{ name: string; amount: number }>('loyalty.apply_promotion', { order, ...input });
        toast(t('promo.applied', { name: r.name, amount: money(r.amount) }));
      }
      if (remove) toast(t('promo.removed'));
      onDone(await action<OrderView>('restaurant.order', { order }));
      onClose();
    } catch (err) {
      fail(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t('promo.till_title')}
      onClose={onClose}
      footer={
        data?.current ? (
          <Button variant="ghost" icon="x" className="dialog-left" busy={busy} onClick={() => void go({}, true)}>
            {t('promo.remove')}
          </Button>
        ) : undefined
      }
    >
      {data?.current ? (
        <p className="callout callout-info" role="status">
          <Icon name="checkCircle" size={16} />
          <span>
            {t('promo.on_bill', { name: data.current.name, amount: money(data.current.amount) })}
            {data.current.stale ? ` ${t('promo.stale')}` : ''}
          </span>
        </p>
      ) : null}
      <form
        className="promo-code-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (code.trim()) void go({ code: code.trim() });
        }}
      >
        <Field label={t('promo.code_label')}>
          <input
            className="input mono"
            value={code}
            maxLength={60}
            autoComplete="off"
            autoCapitalize="characters"
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={t('promo.code_ph')}
            data-autofocus
          />
        </Field>
        <Button type="submit" icon="check" busy={busy} disabled={!code.trim()}>
          {t('promo.use_code')}
        </Button>
      </form>
      <h3 className="promo-offers-title">{t('promo.offers')}</h3>
      {!data ? (
        <Skeleton h={80} />
      ) : !data.offers.length ? (
        <p className="muted">{t('promo.no_offers')}</p>
      ) : (
        <div className="menu-list">
          {data.offers.map((o) => (
            <button key={o.id} className="menu-item" disabled={!!o.why_not || busy} onClick={() => void go({ promotion: o.id })}>
              <Icon name={o.kind === 'buy_get' ? 'gift' : 'percent'} />
              <span>
                <strong>{o.name}</strong>
                <span className="muted">{o.why_not ?? t('promo.takes_off', { amount: money(o.amount) })}</span>
              </span>
              {o.why_not ? null : <strong className="tabular">−{money(o.amount)}</strong>}
            </button>
          ))}
        </div>
      )}
    </Dialog>
  );
}
